import { Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileAlt,
  faDownload,
  faTimes,
  faRedoAlt,
  faCheck,
  faTimesCircle,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { getExtensionOnly } from 'helpers/validators.helper'
import { commmonAllowedFiles } from 'helpers/allowedExtensionFiles.helper'
import { UploadFileStatusEnum } from 'enum/uploadFile-status.enum'
import {
  Wrapper,
  FileInformation,
  Filename,
  FileSize,
  FileActions,
  ProgressBar,
  Error,
} from './styled'
import icons from './icons'

interface IFileBlock {
  data: any
  disabled: boolean
  onDownload?: any
  onRetry?: any
  onDelete?: any
  className?: string
}

const FileBlock: React.FC<IFileBlock> = ({
  className = '',
  data: { name, size, progress, status },
  disabled,
  onRetry,
  onDownload,
  onDelete,
}) => {
  const { t } = useTranslation()
  const translationPrefix = 'reusables.fileUpload.uploadedFiles'

  const getIcon = (name: string) => {
    const extension = getExtensionOnly(name)
    if (icons[extension]) {
      return <img src={icons[extension]} />
    } else {
      const type = commmonAllowedFiles.find((allowedFile) =>
        allowedFile.extension.includes(extension)
      )
      const icon = type && type.icon ? type.icon : faFileAlt
      return <FontAwesomeIcon icon={icon} />
    }
  }

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  return (
    <div className={className}>
      <Wrapper>
        {getIcon(name)}
        <FileActions>
          {!disabled && (
            <Tooltip title={t(`${translationPrefix}.actions.delete.tooltip`)}>
              <FontAwesomeIcon icon={faTimes} onClick={onDelete} />
            </Tooltip>
          )}
          {status !== UploadFileStatusEnum.inProcess && (
            <>
              {status === UploadFileStatusEnum.completed ? (
                <Tooltip
                  title={t(`${translationPrefix}.actions.download.tooltip`)}
                >
                  <FontAwesomeIcon icon={faDownload} onClick={onDownload} />
                </Tooltip>
              ) : disabled ? (
                <Tooltip
                  title={t(`${translationPrefix}.actions.retry.tooltip`)}
                >
                  <FontAwesomeIcon icon={faRedoAlt} onClick={onRetry} />
                </Tooltip>
              ) : (
                <></>
              )}
            </>
          )}
        </FileActions>
        <FileInformation>
          <Filename>
            {name || ''}
            {status === UploadFileStatusEnum.completed && (
              <FontAwesomeIcon icon={faCheck} />
            )}
          </Filename>
          <FileSize>{formatBytes(size)}</FileSize>
          {status === UploadFileStatusEnum.error && (
            <Error>
              <FontAwesomeIcon icon={faTimesCircle} />
              {`${t(`${translationPrefix}.errors.failedToLoadFile.title`)} `}
              <span onClick={() => window.open('/import-center')}>
                {t(`${translationPrefix}.errors.failedToLoadFile.link`)}
              </span>
              {` ${t(`${translationPrefix}.errors.failedToLoadFile.suffix`)}`}
            </Error>
          )}
        </FileInformation>
        {status === UploadFileStatusEnum.inProcess && (
          <ProgressBar progress={progress}>
            <span>{Math.round(progress)} %</span>
            <div>
              <div style={{ width: progress + '%' }} />
            </div>
          </ProgressBar>
        )}
      </Wrapper>
    </div>
  )
}

export default FileBlock
